<template>
  <div class="complexity-slider-input">
    <!-- Category Sliders -->
    <div class="space-y-2" :style="{ '--complexity-label-width': labelColumnWidth }">
      <CategorySlider
        v-for="category in categories"
        :key="category.key"
        :modelValue="getSliderValue(category.key)"
        :label="dimensionLabels[category.key]"
        :categoryKey="category.key"
        :hasJustification="hasJustification(category.key)"
        :isLocked="isSliderLocked(category.key)"
        :role="props.role"
        @update:modelValue="(val) => updateSliderValue(category.key, val)"
        @openJustification="openJustificationModal(category.key)" />
    </div>

    <!-- Complexity Factor Display -->
    <div class="flex items-center justify-between mt-4 pt-4 border-t border-neutral-300">
      <span class="font-medium text-neutral-700">{{ wt('complexity', 'slider', 'complexityFactor') }}</span>
      <span class="text-xl font-bold text-primary-default">{{ complexityFactor.toFixed(2) }}</span>
    </div>

    <!-- Justification Modal -->
    <ComplexityJustificationModal
      :visible="modalVisible"
      :category="currentCategory"
      :categoryLabel="currentCategoryLabel"
      :justificationValue="currentJustificationValue"
      :role="props.role"
      @save="handleJustificationSave"
      @delete="handleJustificationDelete"
      @close="closeModal" />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, toRef, type PropType } from 'vue'
import type { UsageCorrection } from '@/schemas/general/types'
import { useWidgetText } from '@/helpers/useWidgetText'
import { useCalculatorForRole, type Role } from '@/stores/projectCenter/useCalculatorForRole'
import CategorySlider from './CategorySlider.vue'
import ComplexityJustificationModal, {
  type ComplexityCategory,
} from '@/components/partials/modals/ComplexityJustificationModal.vue'

const DEFAULT_SLIDER_VALUE = 1.0

interface CategoryConfig {
  key: ComplexityCategory
  sliderField: 'sliderCreative' | 'sliderTechnical' | 'sliderSpatial' | 'sliderOrganizational'
  justificationField:
    | 'justificationCreative'
    | 'justificationTechnical'
    | 'justificationSpatial'
    | 'justificationOrganizational'
  legacyProblemField: 'problem1' | 'problem2' | 'problem3' | 'problem4'
}

const categories: CategoryConfig[] = [
  {
    key: 'creative',
    sliderField: 'sliderCreative',
    justificationField: 'justificationCreative',
    legacyProblemField: 'problem1',
  },
  {
    key: 'technical',
    sliderField: 'sliderTechnical',
    justificationField: 'justificationTechnical',
    legacyProblemField: 'problem2',
  },
  {
    key: 'spatial',
    sliderField: 'sliderSpatial',
    justificationField: 'justificationSpatial',
    legacyProblemField: 'problem3',
  },
  {
    key: 'organizational',
    sliderField: 'sliderOrganizational',
    justificationField: 'justificationOrganizational',
    legacyProblemField: 'problem4',
  },
]

const modelValue = defineModel({ type: Object as PropType<UsageCorrection> })
const props = defineProps<{ usageIndex: number; role?: Role }>()
const { wt } = useWidgetText('widget-texts-project-setup', toRef(props, 'role'))

const setupCalculator = useCalculatorForRole(toRef(props, 'role'))

// Modal state
const modalVisible = ref(false)
const currentCategory = ref<ComplexityCategory>('creative')

const currentCategoryConfig = computed(() => categories.find((c) => c.key === currentCategory.value) || categories[0])

const currentCategoryLabel = computed(() => wt('complexity', 'dimensions', currentCategory.value, 'title'))

const dimensionLabels = computed<Record<ComplexityCategory, string>>(() => ({
  creative: wt('complexity', 'dimensions', 'creative', 'title'),
  technical: wt('complexity', 'dimensions', 'technical', 'title'),
  spatial: wt('complexity', 'dimensions', 'spatial', 'title'),
  organizational: wt('complexity', 'dimensions', 'organizational', 'title'),
}))

const labelColumnWidth = computed(() => {
  const longestLabelLength = Math.max(...Object.values(dimensionLabels.value).map((label) => label.length))
  const widthInCh = Math.max(longestLabelLength + 1, 6)
  return `min(${widthInCh}ch, 16rem)`
})

const currentJustificationValue = computed(() => {
  if (!modelValue.value) return null
  return modelValue.value[currentCategoryConfig.value.justificationField] || null
})

// Get slider value for a category
function getSliderValue(categoryKey: ComplexityCategory): number {
  if (!modelValue.value) return DEFAULT_SLIDER_VALUE
  const config = categories.find((c) => c.key === categoryKey)
  if (!config) return DEFAULT_SLIDER_VALUE
  return modelValue.value[config.sliderField] ?? DEFAULT_SLIDER_VALUE
}

// Update slider value
function updateSliderValue(categoryKey: ComplexityCategory, value: number) {
  if (!modelValue.value) return
  const config = categories.find((c) => c.key === categoryKey)
  if (!config) return

  modelValue.value = {
    ...modelValue.value,
    [config.sliderField]: value,
  }
  setupCalculator.value.setComplexityForUsages()
}

// Check if category has justification (field must have at least 10 characters)
function hasJustification(categoryKey: ComplexityCategory): boolean {
  if (!modelValue.value) return false
  const config = categories.find((c) => c.key === categoryKey)
  if (!config) return false
  const justification = modelValue.value[config.justificationField]
  return !!(justification && justification.length >= 10)
}

// Check if slider is locked (no justification for non-default values)
function isSliderLocked(categoryKey: ComplexityCategory): boolean {
  if (!modelValue.value) return true
  const config = categories.find((c) => c.key === categoryKey)
  if (!config) return true

  // Slider is locked if no justification
  return !hasJustification(categoryKey)
}

// Calculate complexity factor (rounded to nearest 0.05)
const complexityFactor = computed(() => {
  if (!modelValue.value) return DEFAULT_SLIDER_VALUE

  const values = categories.map((c) => modelValue.value![c.sliderField] ?? DEFAULT_SLIDER_VALUE)
  const average = values.reduce((sum, v) => sum + v, 0) / values.length
  return Math.round((average + Number.EPSILON) * 20) / 20
})

// Modal handlers
function openJustificationModal(categoryKey: ComplexityCategory) {
  currentCategory.value = categoryKey
  modalVisible.value = true
}

function closeModal() {
  modalVisible.value = false
}

function handleJustificationSave(justification: string) {
  if (!modelValue.value) return

  const config = currentCategoryConfig.value
  modelValue.value = {
    ...modelValue.value,
    [config.justificationField]: justification,
  }

  modalVisible.value = false
}

function handleJustificationDelete() {
  if (!modelValue.value) return

  const config = currentCategoryConfig.value

  modelValue.value = {
    ...modelValue.value,
    [config.justificationField]: null,
    [config.sliderField]: DEFAULT_SLIDER_VALUE,
  }

  setupCalculator.value.setComplexityForUsages()
  modalVisible.value = false
}
</script>

<style scoped>
.complexity-slider-input {
  @apply bg-white overflow-visible;
}
</style>
